import { isGemXDevMode } from '~/server/auth/shopify/helpers';
import { getConfig } from '~/config/environment';
import { getShopifyConfig } from '~/config';
import { useCallback } from 'react';

interface ILocationHandlers {
  /**
   * Get GemX admin URL
   * @param pathName [app]/[pathName] Example: 'app/pricing'
   * @returns 'https://admin.shopify.com/store/[shopifyDomain]/apps/[gemxAppHandle]/[pathName]'
   */
  getGemXAdminUrl: (pathName: string) => string;
  /**
   * Get Shopify admin URL
   * @param pathName [pathName]
   * @returns 'https://admin.shopify.com/store/[shopifyDomain]/[pathName]'
   */
  getShopifyAdminUrl: (pathName: string) => string;
  /**
   * Handle opening the url in the current window
   * @param url
   */
  handleOpenUrl: (url?: string) => void;
  /**
   * Handle opening the url in a new tab
   * @param url
   * @param options.noopener Strip `window.opener` from the new tab. Use for untrusted/external
   * destinations (e.g. a merchant's storefront) so their scripts can't navigate this tab away.
   */
  handleOpenUrlInNewTab: (url?: string, options?: { noopener?: boolean }) => void;
  /**
   * Handle replacing the current window location
   * @param pathName [app]/[pathName] Example: 'app/pricing'
   */
  handleReplaceLocation: (pathName: string) => void;
  /**
   * Handle opening the GP page in a new tab
   * @param pathName [app]/[pathName] Example: 'app/pricing'
   */
  handleOpenPageGPInNewTab: (pathName: string) => void;
  /**
   * Handle opening the GemX page in a new tab
   * @param pathName [app]/[pathName] Example: 'app/campaigns'
   */
  handleOpenPageGemXInNewTab: (pathName: string) => void;
  /**
   * Handle opening the Shopify page in a new tab
   * @param pathName [pathName] Example: 'themes' or 'products'
   */
  handleOpenPageShopifyInNewTab: (pathName: string) => void;
}

export const useLocationHandlers = (): ILocationHandlers => {
  const shopifyConfig = getShopifyConfig();
  const gemxAppHandle = getConfig('APP')?.GEMX_APP_HANDLE;
  const gempagesAppHandle = getConfig('APP')?.GEMPAGES_APP_HANDLE;
  const shopifyAdminDomain = getConfig('APP')?.SHOPIFY_ADMIN_DOMAIN;
  const shopifyAppUrl = getConfig('APP')?.SHOPIFY_APP_URL;

  const shopifyDomain = useCallback(
    () => shopifyConfig?.shopifyDomain?.split('.')[0] ?? '',
    [shopifyConfig?.shopifyDomain],
  );

  const getShopifyAdminUrl = (pathName: string) => `shopify://admin/${pathName}`;

  // Let App Bridge handle the `shopify://` deep link (works on mobile too). Don't
  // special-case mobile with a raw top-level nav — it strands the user in the target app.
  const openAdminPathInNewTab = (adminPath: string) => {
    window.open(getShopifyAdminUrl(adminPath), '_blank');
  };

  const getGemXAdminUrl = (pathName: string) => {
    const cleanedPathName = pathName.replace(/^\/+/, '');
    if (isGemXDevMode()) {
      return `${shopifyAppUrl}${cleanedPathName}`;
    }

    return `${shopifyAdminDomain}store/${shopifyDomain()}/apps/${gemxAppHandle}/${cleanedPathName}`;
  };

  const handleReplaceLocation = (pathName: string) => {
    const adminUrl = getShopifyAdminUrl(`apps/${gemxAppHandle}/${pathName}`);
    window.top?.location.replace(adminUrl);
  };

  const handleOpenPageGemXInNewTab = (pathName: string) => {
    // Validate input
    if (!pathName || typeof pathName !== 'string') {
      throw new Error('pathName must be a non-empty string');
    }

    // Remove leading '/' if present
    const cleanedPathName = pathName.replace(/^\/+/, '');

    // Construct the Shopify admin URL
    openAdminPathInNewTab(`apps/${gemxAppHandle}/${cleanedPathName}`);
  };

  const handleOpenPageGPInNewTab = (pathName: string) => {
    openAdminPathInNewTab(`apps/${gempagesAppHandle}/${pathName}`);
  };

  const handleOpenPageShopifyInNewTab = (pathName: string) => {
    openAdminPathInNewTab(pathName);
  };

  const handleOpenUrlInNewTab = (url?: string, options?: { noopener?: boolean }) => {
    url && window.open(url, '_blank', options?.noopener ? 'noopener,noreferrer' : undefined);
  };

  const handleOpenUrl = (url?: string) => {
    if (!url) return;

    const isInIframe = window.self !== window.top;
    if (isInIframe && window.top) {
      window.top.location.href = url;

      return;
    }

    window.open(url);
  };

  return {
    getGemXAdminUrl,
    getShopifyAdminUrl,
    handleOpenUrl,
    handleOpenUrlInNewTab,
    handleReplaceLocation,
    handleOpenPageGPInNewTab,
    handleOpenPageGemXInNewTab,
    handleOpenPageShopifyInNewTab,
  };
};
